<template>
    <div class="flex flex-col items-center gap-[60px] w-full max-w-3xl">
        <div v-if="greetingFlow && greetingFlow.length > 0">
            <div class="flex justify-center gap-2 mb-[60px]">
                <button
                    v-for="(page, index) in greetingFlow"
                    :key="index"
                    :aria-label="'Go to page ' + (index + 1)"
                    :class="[
                        'transition-all duration-200',
                        currentPage === index ? 'w-8 h-2 bg-valence-primary-500 rounded-full' : 'w-2 h-2 bg-gray-300 rounded-full hover:bg-gray-400',
                    ]"
                    @click="currentPage = index" />
            </div>

            <Transition name="slide-fade" mode="out-in">
                <div :key="currentPage" class="text-center w-full mb-[60px] min-h-[300px] md:min-h-[250px] flex flex-col justify-start">
                    <div v-if="currentPage === 0" class="mb-8">
                        <span class="gradient-text display-medium">Hi {{ firstName }}, I'm Nadia.</span>
                    </div>

                    <h2 class="display-medium text-balance mb-6">
                        {{ greetingFlow[currentPage]?.title }}
                    </h2>

                    <p class="text-valence-grey-750 title-body max-w-2xl mx-auto text-pretty">
                        {{ greetingFlow[currentPage]?.subtitle }}
                    </p>
                </div>
            </Transition>

            <div class="flex justify-center items-center w-full gap-4">
                <BaseButton v-if="currentPage > 0" theme="tertiary" @click="currentPage--">Back</BaseButton>

                <BaseButton v-if="currentPage < greetingFlow.length - 1" theme="primary" data-testid="greeting-next" @click="currentPage++"> Next </BaseButton>
                <BaseButton v-else theme="primary" data-testid="get-started" @click="handleGetStarted"> Get started </BaseButton>
            </div>
        </div>

        <div v-else class="text-center leading-tight">
            <div class="max-w-xl display-medium mb-[60px]">
                <div class="mb-3">
                    <span class="gradient-text">Hi {{ firstName }}, I'm Nadia.</span>
                    <br />A safe space to think, reflect, and grow together.
                </div>
                <p class="text-valence-grey-750 text-center display-serif">
                    I'm here to help you navigate work challenges and enhance your impact, as {{ prefixArticle(role) }}{{ organizationName ? ` at ${organizationName}` : "" }}.
                </p>
            </div>
            <BaseButton theme="primary" class="w-[300px] leading-6 body-small" data-testid="get-started" @keydown.enter="handleGetStarted" @click="handleGetStarted">
                Get started
            </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { prefixArticle } from "~vue/utils/stringUtils";
import { ref } from "vue";

interface GreetingPage {
    title: string;
    subtitle: string;
}

interface Props {
    greetingFlow?: GreetingPage[];
    firstName: string;
    role?: string;
    organizationName?: string;
}

withDefaults(defineProps<Props>(), {
    greetingFlow: () => [],
    role: "",
    organizationName: "",
});

const emit = defineEmits<{
    getStarted: [];
}>();

const currentPage = ref<number>(0);

function handleGetStarted(): void {
    emit("getStarted");
}
</script>

<style scoped>
/* Slide transition for multi-page greeting carousel */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.3s ease;
}

.slide-fade-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.slide-fade-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}
</style>
